import { Avatar, Box, FormControlLabel, IconButton, Menu, MenuItem, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { ColorModeContext } from "../../theme";
import LanguageSelect from "../../components/LanguageSelect";
import ThemeToggle from "../../components/dashboard/ThemeToggle";
import NotificationCenter from "../../components/enterprise/NotificationCenter";
import AlertBanner from "../../components/AlertBanner";
import { usePlatform } from "../../context/PlatformContext";
import { useAlertVisuals } from "../../context/AlertVisualContext";
import { hasPermission } from "../../utils/permissions";

const Topbar = ({ user, role, onLogout, onOpenNavigation }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const colorMode = useContext(ColorModeContext);
  const { showMockData, setShowMockData } = usePlatform();
  const isVigilante = role === "vigilante";
  const isRealSuperadmin = role === "superadmin" && !sessionStorage.getItem("superadmin_token");
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { recentAlerts, unseenCount } = useAlertVisuals();

  return (
    <Box
      component="header"
      className="platform-topbar"
      sx={{
        position: "sticky",
        top: 24,
        zIndex: 20,
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2.5 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        backdropFilter: theme.custom.glass.backdropFilter,
        background: theme.custom.surface.overlay,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.custom.navigationRadius,
        boxShadow: theme.custom.glass.boxShadow,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} minWidth={0} sx={{ flex: { xs: "1 1 100%", lg: "1 1 auto" }, order: { xs: 1, lg: 0 }, "&:empty": { display: "none" } }}>
        {!isVigilante && !isRealSuperadmin && <AlertBanner />}
      </Stack>

      <Stack direction="row" alignItems="center" gap={0.5} sx={{ width: { xs: "100%", lg: "auto" }, ml: "auto", order: { xs: 0, lg: 1 }, flexWrap: "wrap", "& .MuiIconButton-root": { width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } } }}>
        {onOpenNavigation && <IconButton aria-label={t("platform.sidebar.expand")} onClick={onOpenNavigation} sx={{ mr: "auto" }}><MenuOutlinedIcon /></IconButton>}
        {!isVigilante && process.env.NODE_ENV !== "production" && (
        <FormControlLabel
            control={<Switch size="small" checked={showMockData} onChange={(event) => setShowMockData(event.target.checked)} />}
            label={<Box><Typography variant="body2" color="text.primary" sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("platform.topbar.showMockData")}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>{t(showMockData ? "platform.reference.simulationOn" : "platform.reference.simulationOff")}</Typography></Box>}
            sx={{
          ml: 0, mr: { xs: "auto", lg: 1 }, gap: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 1.5 }, py: 0.75,
          flexDirection: "row-reverse", border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "action.hover",
              color: showMockData ? "primary.main" : "text.secondary",
              "& .MuiFormControlLabel-label": { fontSize: 12, fontWeight: 500 },
            }}
          />
        )}
        {!isVigilante && !isRealSuperadmin && (
          <NotificationCenter
            count={unseenCount}
            items={recentAlerts.map((alert) => ({
              id: alert.id,
              detectionType: alert.detection_type,
              title: String(alert.detection_type || t("platform.notifications.criticalAlert")).replace(/_/g, " "),
              detail: `${alert.camera_display_name || alert.camera_name || t("platform.common.camera")} · ${alert.severity || ""}`,
            }))}
          />
        )}
        <Tooltip title={user?.username || user?.email || t("platform.sidebar.user")}>
          <IconButton aria-label={t("platform.sidebar.user")} aria-haspopup="menu" aria-expanded={Boolean(userMenuAnchor)} onClick={(event) => setUserMenuAnchor(event.currentTarget)} sx={{ width: 36, height: 36 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: "action.selected", color: "primary.main", fontSize: 12, fontWeight: 600 }}>
              {(user?.username || user?.email || "U").slice(0, 1).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Stack>
      <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
        {!isVigilante && <MenuItem component={Link} to="/settings" onClick={() => setUserMenuAnchor(null)}><SettingsOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />{t("topbar.settings")}</MenuItem>}
        {!isVigilante && hasPermission(role, "billing:manage") && <MenuItem component={Link} to="/billing" onClick={() => setUserMenuAnchor(null)}><ReceiptLongOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />{t("menu.billing")}</MenuItem>}
        {!isVigilante && <MenuItem component={Link} to="/support" onClick={() => setUserMenuAnchor(null)}><SupportAgentOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />{t("platform.secondary.support", "Soporte")}</MenuItem>}
        {isRealSuperadmin && <MenuItem component={Link} to="/superadmin" onClick={() => setUserMenuAnchor(null)}><AdminPanelSettingsOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />{t("platform.secondary.superadmin", "Consola Superadmin")}</MenuItem>}
        <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography variant="body2">{t("common.language")}</Typography><LanguageSelect />
          <ThemeToggle mode={colorMode.currentMode} onToggle={colorMode.toggleColorMode} />
        </Box>
        <MenuItem onClick={() => { setUserMenuAnchor(null); onLogout(); }}><LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />{t("topbar.logout")}</MenuItem>
      </Menu>
    </Box>
  );
};

export default Topbar;
